import { Switch } from "@headlessui/react";
import { Icon } from "@iconify/react";
import React, { useState } from "react";
import { toast } from "react-toastify";
import UpdateHooks from "../../../../Hooks/UpdateHooks";
import moment from "moment";

const AdminPatientGeneral = ({ patientId, patient }) => {
  const [account, setAccount] = useState(patient?.isActive);
  const [selectedOption, setSelectedOption] = useState(patient?.accountType);
  const [password, setPassword] = useState("");

  const generatePassword = (event) => {
    event.preventDefault();
    const chars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let generatedPassword = "";
    for (let i = 0; i < 8; i++) {
      generatedPassword += chars[Math.floor(Math.random() * chars.length)];
    }
    setPassword(generatedPassword);
  };

  const handelUpdate = async (e) => {
    e.preventDefault();
    const name = e.target?.name.value;
    const email = e.target?.email.value;
    const dateOfBirth = e.target?.dateOfBirth.value;
    const phone = e.target?.phone.value;

    const payload = {
      name: name,
      phone: phone,
      dateOfBirth: dateOfBirth,
      email: email,
    };
    if (password) {
      payload.password = password;
    }

    await UpdateHooks(`https://servercareseebd.careseebd.com/api/v1/user/${patientId}`, payload);
    toast.success("Profile configurations updated!");
  };

  const handelUpdateAccount = async () => {
    await UpdateHooks(`https://servercareseebd.careseebd.com/api/v1/user/${patientId}`, {
      isActive: !account,
    });
    toast.success("Account status updated!");
  };

  const handelUpdateAccountType = async () => {
    await UpdateHooks(`https://servercareseebd.careseebd.com/api/v1/user/${patientId}`, {
      accountType: selectedOption ? selectedOption : "none",
    });
    toast.success("Account type updated!");
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Top Status & Type Row */}
      <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {/* Account Active Switch */}
        <div className="flex items-center gap-3">
          <Switch
            checked={account}
            onChange={(checked) => {
              setAccount(checked);
              handelUpdateAccount();
            }}
            className={`${account ? "bg-secondary" : "bg-slate-200"
              } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
          >
            <span
              aria-hidden="true"
              className={`${account ? "translate-x-5" : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out`}
            />
          </Switch>
          <div className="space-y-0.5">
            <span className="block text-xs font-bold text-slate-700">Account Access State</span>
            <span className="block text-[10px] font-semibold text-slate-450 uppercase">
              {account ? "Active / Allowed Login" : "Inactive / Suspended"}
            </span>
          </div>
        </div>

        {/* Therapy Account Type Selector */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-600">
              <input
                type="radio"
                name="option"
                checked={selectedOption === "convergence"}
                value="convergence"
                onChange={(e) => setSelectedOption(e.target.value)}
                className="w-3.5 h-3.5 text-secondary border-slate-300 focus:ring-secondary/20"
              />
              <span>Convergence</span>
            </label>

            <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-600">
              <input
                type="radio"
                name="option"
                checked={selectedOption === "divergence"}
                value="divergence"
                onChange={(e) => setSelectedOption(e.target.value)}
                className="w-3.5 h-3.5 text-secondary border-slate-300 focus:ring-secondary/20"
              />
              <span>Divergence</span>
            </label>

            <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-slate-600">
              <input
                type="radio"
                name="option"
                checked={selectedOption === "none"}
                value="none"
                onChange={(e) => setSelectedOption(e.target.value)}
                className="w-3.5 h-3.5 text-secondary border-slate-300 focus:ring-secondary/20"
              />
              <span>None</span>
            </label>
          </div>

          <button
            onClick={handelUpdateAccountType}
            className="bg-secondary hover:bg-secondary/95 text-white font-bold py-1.5 px-4 rounded-xl shadow-xs transition text-xs cursor-pointer"
          >
            Update Type
          </button>
        </div>
      </div>

      {/* Main Forms fields */}
      <form onSubmit={handelUpdate} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Full Name</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <Icon icon="solar:user-linear" className="w-4 h-4" />
              </div>
              <input
                type="text"
                name="name"
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-xs font-semibold text-slate-700 bg-white"
                defaultValue={patient?.name}
                required
              />
            </div>
          </div>

          {/* Email Address */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Email Address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <Icon icon="solar:letter-linear" className="w-4 h-4" />
              </div>
              <input
                type="email"
                name="email"
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-xs font-semibold text-slate-700 bg-white"
                defaultValue={patient?.email}
                required
              />
            </div>
          </div>

          {/* Date of Birth info */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">
              Date of birth :{" "}
              {patient?.dateOfBirth ? (
                <span className="text-secondary font-extrabold">{moment(patient.dateOfBirth).format("LL")}</span>
              ) : (
                <span className="text-slate-400 font-extrabold">Not Set</span>
              )}{" "}
              | Age :{" "}
              {patient?.dateOfBirth ? (
                <span className="text-secondary font-extrabold">{moment().diff(patient.dateOfBirth, "years")}</span>
              ) : (
                <span className="text-slate-400 font-extrabold">Not Set</span>
              )}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <Icon icon="solar:calendar-date-linear" className="w-4 h-4" />
              </div>
              <input
                type="date"
                name="dateOfBirth"
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-xs font-semibold text-slate-700 bg-white"
                defaultValue={patient?.dateOfBirth}
              />
            </div>
          </div>

          {/* Phone Number */}
          <div className="space-y-1.5">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Phone Number</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <Icon icon="solar:phone-linear" className="w-4 h-4" />
              </div>
              <input
                type="tel"
                name="phone"
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-xs font-semibold text-slate-700 bg-white"
                defaultValue={patient?.phone}
                required
              />
            </div>
          </div>

          {/* Password field with Generator */}
          <div className="space-y-1.5 md:col-span-2">
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Update Login Password</label>
            <div className="relative flex items-stretch border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-secondary transition bg-white">
              <div className="flex items-center pl-3.5 text-slate-400">
                <Icon icon="solar:lock-password-linear" className="text-sm" />
              </div>
              <input
                type="text"
                value={password}
                name="password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter or generate password to reset"
                className="flex-1 px-3 py-2 focus:outline-none text-xs font-semibold text-slate-700"
              />
              <button
                type="button"
                onClick={generatePassword}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 border-l border-slate-200 text-slate-650 font-bold text-xs transition duration-155 cursor-pointer"
              >
                Generate
              </button>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="pt-2 flex justify-end">
          <button
            type="submit"
            className="bg-secondary hover:bg-secondary/95 text-white font-bold py-2.5 px-6 rounded-xl shadow-md shadow-indigo-900/10 hover:shadow-lg transition duration-200 hover:-translate-y-0.5 active:translate-y-0 text-xs flex items-center gap-1.5 cursor-pointer"
          >
            <Icon icon="solar:diskette-bold" className="text-sm" />
            <span>Update General Info</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminPatientGeneral;
